import { FC, useEffect, useState } from 'react';
import {
  VStack,
  Button,
  useToast,
  Spinner,
  Center,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Checkbox,
  useDisclosure,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { PageHeading, Panel } from '@bemap/ui';
import {
  FunnelManager,
  SalesFunnel,
  useFunnels,
  useFunnelContext,
} from '@bemap/crm-microfrontends';
import {
  getFunnels,
  createFunnel,
  updateFunnel,
  SalesFunnel as SalesFunnelType,
  CreateSalesFunnelDto,
} from '@/services/crm-service';

const FunnelSettingsPage: FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [funnelName, setFunnelName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const { selectedFunnel, setSelectedFunnelId, setFunnels } = useFunnelContext();

  const fetchFunnels = async () => {
    const res = await getFunnels();
    return (res.message || []) as SalesFunnelType[];
  };

  const { funnels, loading, refetch } = useFunnels(fetchFunnels);

  useEffect(() => {
    if (funnels.length > 0) {
      setFunnels(funnels);
      // If no funnel is selected and we have funnels, select the default or first one
      if (!selectedFunnel && funnels.length > 0) {
        const defaultFunnel = funnels.find(f => f.is_default) || funnels[0];
        if (defaultFunnel) {
          setSelectedFunnelId(defaultFunnel.id);
        }
      }
    }
  }, [funnels, setFunnels, selectedFunnel, setSelectedFunnelId]);

  const handleCreateFunnel = async () => {
    if (!funnelName.trim()) {
      toast({ status: 'error', title: t('general.name_required') || 'Name is required' });
      return;
    }

    try {
      const payload: CreateSalesFunnelDto = {
        name: funnelName,
        steps: [
          { id: 'todo', name: 'To Do', order: 0, color: '#3182CE' },
          { id: 'doing', name: 'Doing', order: 1, color: '#ED8936' },
          { id: 'done', name: 'Done', order: 2, color: '#38A169' },
        ],
        is_default: isDefault,
      };
      await createFunnel(payload);
      toast({ status: 'success', title: t('general.created') || 'Created' });
      setFunnelName('');
      setIsDefault(false);
      onClose();
      await refetch();
      // After refetch, funnels will be updated and we can select the new one
      // We'll handle selection in useEffect when funnels change
    } catch (error) {
      toast({ status: 'error', title: t('general.error') || 'Error' });
    }
  };

  const handleUpdateFunnel = async (funnel: Partial<SalesFunnel>) => {
    if (!selectedFunnel?.id) return;

    try {
      await updateFunnel(selectedFunnel.id, {
        name: funnel.name ?? selectedFunnel.name,
        steps: funnel.steps ?? selectedFunnel.steps,
        is_default: funnel.is_default ?? selectedFunnel.is_default,
      });
      toast({ status: 'success', title: t('general.updated') || 'Updated' });
      // Refetch funnels to get the latest data
      await refetch();
      // The useEffect will update the context when funnels change
    } catch (error) {
      console.error('Error updating funnel:', error);
      toast({ status: 'error', title: t('general.error') || 'Error' });
    }
  };

  // removed unused handleDeleteFunnel

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <VStack w="100%" gap={4} align="stretch">
      <PageHeading
        title={t('crm.funnel_settings') || 'Funnel Settings'}
        actions={[
          {
            label: t('actions.new_masculine') + ' ' + (t('crm.funnel') || 'Funnel'),
            onClick: onOpen,
          },
        ]}
      />
      <Panel>
        <VStack align="stretch" gap={4}>
          {selectedFunnel ? (
            <FunnelManager
              key={selectedFunnel.id}
              funnel={selectedFunnel}
              onUpdate={handleUpdateFunnel}
            />
          ) : funnels.length === 0 ? (
            <Center h="200px">
              {t('crm.no_funnels') || 'No funnels created yet. Create your first funnel to get started.'}
            </Center>
          ) : (
            <Center h="200px">
              {t('crm.no_funnel') || 'No funnel selected. Please select a funnel from the header.'}
            </Center>
          )}
        </VStack>
      </Panel>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('crm.create_funnel') || 'Create Funnel'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('general.name') || 'Name'}</FormLabel>
                <Input
                  value={funnelName}
                  onChange={(e) => setFunnelName(e.target.value)}
                  placeholder={t('crm.funnel_name_placeholder') || 'e.g., Sales Pipeline'}
                />
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={isDefault}
                  onChange={(e) => setIsDefault(e.target.checked)}
                >
                  {t('crm.set_as_default') || 'Set as default funnel'}
                </Checkbox>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('actions.cancel') || 'Cancel'}
            </Button>
            <Button colorScheme="blue" onClick={handleCreateFunnel}>
              {t('actions.create') || 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </VStack>
  );
};

export default FunnelSettingsPage;

